import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from '@angular/router';
import { AuthorizationService, ConfirmationDialogService, NotificationDialogService, NotificationPopupService } from '@myapp/modules/fwcore/services';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { Keys } from '@progress/kendo-angular-common';
import { AddEvent, CancelEvent, CellClickEvent, CellCloseEvent, GridModule, RemoveEvent, SaveEvent } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { UserImport } from '../../../models';
import { RequestUserImportSet } from '../../../models/messages';
import { AdminService } from '../../../services';
import { checkboxIcon } from '@progress/kendo-svg-icons';

@Component({
  selector: 'app-user-import',
  templateUrl: './user-import.component.html',
  styleUrls: ['./user-import.component.scss'],
  standalone: true,
  imports: [GridModule, ButtonModule, ReactiveFormsModule]
})

export class UserImportComponent implements OnInit, OnDestroy {

  /*-------------------------- Input / Outputs -------------------------*/

  @Input() public companyId: string | null = null;
  @Input() public licenseId: string | null = null;



  /*-------------------------- public properties / methods -------------*/

  public gridState: State = {
    sort: [],
    skip: 0,
    take: 20
  };

  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();
    ctrlOptions.elemTop = this.elem.nativeElement.getBoundingClientRect().top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    const h = this.controlResizer.calculateHeight(ctrlOptions);
    //this.heightGrid = h;

  }

  /*-------------------------- Internal properties  --------------------*/

  protected dataItems: UserImport[] = [];

  protected heightGrid!: number;

  protected destroy$ = new Subject();

  protected viewInit: boolean = false;

  protected loading: boolean = true;

  /*--------------------------- Constructor  --------------------------*/

  constructor(protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected controlResizer: ControlResizerService
    , protected router: Router
    , protected formBuilder: FormBuilder
    , protected notification: NotificationPopupService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected elem: ElementRef) {

  }

  /*--------------------------- ng  -----------------------------------*/

  ngOnInit(): void {

    this.viewInit = true;
    this.sizeChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  -----------------------*/

  @HostListener('window:resize', ['$event'])
  protected onResize(event: Event) {
    this.sizeChange();
  }

  @HostListener('paste', ['$event'])
  public onPaste(ev: ClipboardEvent) {

    if (ev.target && (ev.target as HTMLElement).tagName && (ev.target as HTMLElement).tagName.match(/(input|textarea)/i)) {

      // Do not handle past when an input element is currently focused
      return;
    }

    ev.preventDefault();

    // Get clipboard data as text
    const data = ev.clipboardData?.getData('text');
    if (data === undefined || data.length < 1)
      return;

    // Simplified parsing of the TSV data with hard-coded columns
    const rows = data.split('\n');

    const result: any[] = rows.map((row: string) => {
      const cells = row.split('\t');
      const r: UserImport = {
        email: cells[0],
        firstName: cells[1],
        alias: cells[2],
        lastName: cells[3],
        isSendEmail: cells[4] ? (cells[4].toLowerCase() === 'true' || cells[4] === '1') : false
      };
      return r;
    });

    this.onExcelPaste(result);
  }

  public onExcelPaste(data: UserImport[] | null): void {
    this.dataItems = data ?? [];
  }

  public onAddRow(args: AddEvent): void {
    args.sender.addRow(this.createFormGroup(new UserImport()));
  }

  public onSaveHandler(args: SaveEvent): void {
    if (args.formGroup.valid) {

      if (args.isNew)
        this.dataItems.unshift(args.dataItem);
      else {

      }

      args.sender.closeRow(args.rowIndex);

    } else {
      args.formGroup.markAllAsTouched();
    }
  }

  public onRemoveHandler(args: RemoveEvent): void {

    const index = this.dataItems.indexOf(args.dataItem, 0);
    if (index > -1) {
      this.dataItems.splice(index, 1);
    }


    args.sender.cancelCell();
  }

  public onCancelHandler(args: CancelEvent): void {
    args.sender.closeRow(args.rowIndex);
  }

  public onCellClickHandler(args: CellClickEvent) {

    if (!args.isEdited) {
      args.sender.editCell(
        args.rowIndex,
        args.columnIndex,
        this.createFormGroup(args.dataItem)
      );
    }
  }

  public onCellCloseHandler(args: CellCloseEvent): void {
    const { formGroup, dataItem } = args;

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (args.originalEvent && args.originalEvent.keyCode === Keys.Escape) {
        return;
      }

      const index = this.dataItems.indexOf(dataItem, 0);
      if (index > -1) {
        this.dataItems.splice(index, 1, formGroup.value);
      }
    }
  }

  public onStateChange(state: State): void {

    this.gridState = state;

  }

  protected onUsersClearAll() {

    this.dialogConfirm.open('Confirm Clear', 'Are you sure you want to clear all the users from the grid?')
      .subscribe((result: any) => {
        if (result.value) {
          this.gridUsersClear();
        }
      });
  }

  protected onImportUsers() {

    const missingFieldRows = this.getRowsMissingRequiredFields();
    if (missingFieldRows.length > 0) {
      this.dialogNotification.openNotificationDialog(
        "Import Users"
        , `Row(s) ${missingFieldRows.join(', ')} are missing a required field (Email, First Name or Last Name). Please complete them before importing.`
      );
      return;
    }

    this.dialogConfirm.open('Confirm upload', 'Are you sure you want to proceed and create the new users?')
      .subscribe((result: any) => {
        if (result.value) {

          const req: RequestUserImportSet = {
            licenseId: this.licenseId
            , companyId: this.companyId
            , data: this.dataItems
          };

          this.userImport(req);
        }
      });
  }

  /** Returns the 1-based row numbers that are missing a required field (Email, First Name or Last Name) */
  protected getRowsMissingRequiredFields(): number[] {

    return this.dataItems.reduce((rows: number[], item, index) => {
      if (!item.email?.trim() || !item.firstName?.trim() || !item.lastName?.trim()) {
        rows.push(index + 1);
      }
      return rows;
    }, []);
  }

  /*--------------------------- internal functions  -------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();

      options.elemTop = 240;
      this.heightGrid = this.controlResizer.calculateHeight(options);
    }
  }

  protected createFormGroup(dataItem: UserImport): FormGroup {

    return this.formBuilder.group({
      email: [dataItem.email, Validators.required],
      firstName: [dataItem.firstName, Validators.required],
      lastName: [dataItem.lastName, Validators.required],
      alias: dataItem.alias,
      isSendEmail: [dataItem.isSendEmail],
    });
  }

  protected gridUsersClear() {
    this.dataItems = [];
  }

  protected userImport(req: RequestUserImportSet) {

    this.adminService.setUserImport$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe({
        next: (res) => {
          if (res.success && res.data && res.data?.length > 0) {

            this.dataItems = res.data;

            const newUsers = res.data.filter(item => item.exists === false).length;
            const existingUsers = res.data.filter(item => item.exists === true).length;

            let msg = `${newUsers} user(s) were added`;
            if (existingUsers > 0)
              msg += ` and ${existingUsers} user(s) already existed`;

            this.dialogNotification.openNotificationDialog("Import Users", msg);

          } else {
            this.dialogNotification.openNotificationDialog("Import Users", "Failed to import users.");
          }
        }
        , error: () => {
          this.dialogNotification.openNotificationDialog("Import Users", "Failed to import users.");
        }
      });
  }

  public onSendEmailChange(event: Event, dataItem: UserImport): void {
    const checked = (event.target as HTMLInputElement).checked;
    dataItem.isSendEmail = checked;
  }

}

